import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const formatServiceName = (name) =>
  name
    ? name.split(/[-_]/).map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(" ")
    : "";

const formatCurrency = (value) =>
  typeof value === "number" && value > 0
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
    : "—";

/** X-axis ticks: compact $ labels */
const formatAxisUsd = (v) => {
  if (typeof v !== "number" || !Number.isFinite(v)) return "";
  if (v >= 1000) return `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  if (Number.isInteger(v)) return `$${v}`;
  return `$${v.toFixed(0)}`;
};

function SummaryCostChart({ overall, services, onServiceBarClick, activeServiceKey = "" }) {
  const data = useMemo(() => {
    const list = Array.isArray(services) ? services : [];
    return list
      .filter((row) => (row.cost_unblended_mtd_total ?? 0) > 0 || (row.arns_cost ?? 0) > 0)
      .slice(0, 8)
      .map((row) => ({
        name: formatServiceName(row.service) || row.service || "—",
        /** Raw API service key for details `service=` filter (must match summary row). */
        serviceKey: row.service || "",
        value: row.cost_unblended_mtd_total ?? 0,
        arns: row.arns_cost ?? 0,
        fill: t.colors.success,
      }))
      .sort((a, b) => b.value - a.value);
  }, [services]);

  const totalArns = overall?.arns_cost ?? 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <Box sx={{ ...t.chartTooltip }}>
        <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.75rem" }}>
          {d.name}
        </Typography>
        <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", display: "block" }}>
          Cost (Last 30 Days): {formatCurrency(d.value)}
        </Typography>
        {d.arns > 0 && (
          <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem" }}>
            ARNs: {d.arns}
          </Typography>
        )}
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No cost data (last 30 days)
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        height: "100%",
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.875rem", mb: 1, flexShrink: 0 }}>
        Cost (Last 30 days)
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          width: "100%",
          position: "relative",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 16, left: 8, bottom: 28 }}
            barCategoryGap={data.length <= 4 ? "12%" : "8%"}
            onClick={(state) => {
              if (!onServiceBarClick || !state?.activePayload?.length) return;
              const key = state.activePayload[0]?.payload?.serviceKey;
              if (key) onServiceBarClick(key);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={t.colors.borderLight} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: t.colors.textMuted }}
              tickFormatter={formatAxisUsd}
              label={{
                value: "Cost (USD, last 30 days)",
                position: "bottom",
                offset: 4,
                style: { fill: t.colors.textMuted, fontSize: 11, fontWeight: 500, fontFamily: "system-ui, sans-serif" },
              }}
            />
            <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 10, fill: t.colors.text }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
            <Bar
              dataKey="value"
              nameKey="name"
              radius={[0, 4, 4, 0]}
              maxBarSize={data.length <= 4 ? 32 : 24}
              fill={t.colors.success}
              cursor={onServiceBarClick ? "pointer" : "default"}
            >
              {data.map((entry) => {
                const hasSel = Boolean(activeServiceKey);
                const selected = hasSel && entry.serviceKey === activeServiceKey;
                return (
                  <Cell
                    key={entry.serviceKey || entry.name}
                    fill={t.colors.success}
                    fillOpacity={hasSel ? (selected ? 1 : 0.22) : 1}
                    stroke={selected ? t.colors.accent : "transparent"}
                    strokeWidth={selected ? 2.5 : 0}
                    style={
                      hasSel
                        ? selected
                          ? { filter: "drop-shadow(0 0 10px rgba(59,130,246,0.45))" }
                          : { filter: "blur(1.2px)" }
                        : undefined
                    }
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default SummaryCostChart;
